import { exhaustiveCaseGuard, forceCheckedIndexedAccess, lazyRef, noAvailableOptions, sortBy, sortByMany, uniqueBy } from "src/helpers/utils"
import { computed, defineComponent, onMounted, ref } from "vue"
import { MutUiSelection } from "../GameScheduler/calendar/PracticeSchedulerActions"
import { GlobalInteractionBlockingRequestsInFlight } from "src/store/EventuallyPinia"
import { Guid, MinCompetition, MinDivision, MinSeason, MinTeam } from "src/interfaces/InleagueApiV1"
import { CoachAssignmentsTeamSummaryTeam, CoachAssignmentUser, createCoachAndVolunteerAssignments, deleteCoachAndVolunteerAssignments, getAssignableCodesForUsers, getCoachAssignmentMenuOptions, getCoachAssignmentsTeamsSummary, listCoachAssignmentCandidates, listCoachAssignmentVolunteers, searchCoachAssignmentCandidates } from "./CoachAssignments.io"
import { axiosAuthBackgroundInstance, axiosInstance } from "src/boot/AxiosInstances"
import { FormKit } from "@formkit/vue"
import { ReactiveReifiedPromise } from "src/helpers/ReifiedPromise"
import { teamDesignationAndMaybeName } from "../GameScheduler/calendar/GameScheduler.shared"
import { CoachAssignmentCandidateSearch, CoachAssignmentFormInfo, TeamSummaryElement, CurrentTeamAssignmentsListingElement, VolAssignmentsToDeleteForm, ConfirmDeleteTeamAssignmentsModal } from "./CoachAssignments.02"
import { AxiosErrorWrapper } from "src/boot/AxiosErrorWrapper"
import { AutoModal, DefaultModalController_r, DefaultTinySoccerballBusyOverlay, useDefaultNoCloseModalIfBusy } from "../UserInterface/Modal"
import iziToast from "izitoast"
import { getCompetitions } from "src/composables/InleagueApiV1"

export default defineComponent({
  setup() {
    const ready = ref(false)

    const menu = lazyRef<MenuSeasons>()
    type MenuSeasons = Map<Guid, {season: MinSeason, competitions: MenuComps}>
    type MenuComps = Map<Guid, {competition: MinCompetition, divisions: MenuDivs}>
    type MenuDivs = Map<Guid, {division: MinDivision, teams: MenuTeams}>
    type MenuTeams = Map<Guid, MinTeam>

    const selectedSeason = lazyRef<MutUiSelection<Guid, MinSeason | null>>()
    const seasonOptions = computed(() => {
      const seasons = [...menu.value.values()].map(v => v.season)
      return seasons.length === 0
        ? noAvailableOptions()
        : {disabled: false, options: seasons.map(v => ({label: v.seasonName, value: v.seasonUID}))}
    })

    const selectedCompetition = lazyRef<MutUiSelection<Guid, MinCompetition | null>>()
    const competitionOptions = computed(() => {
      const competitions = [
        ...menu.value.get(selectedSeason.value.selectedKey)
          ?.competitions.values() ?? []
      ].map(v => v.competition)
      return competitions.length === 0
        ? noAvailableOptions()
        : {disabled: false, options: competitions.map(v => ({label: v.competition, value: v.competitionUID}))}
    })

    const selectedDivision = lazyRef<MutUiSelection<Guid, MinDivision | null>>()
    const divisionOptions = computed(() => {
      const divisions = [
        ...menu.value.get(selectedSeason.value.selectedKey)
          ?.competitions
          .get(selectedCompetition.value.selectedKey)?.divisions.values() ?? []
      ].map(v => v.division)
      return divisions.length === 0
        ? noAvailableOptions()
        : {disabled: false, options: divisions.map(v => ({label: v.displayName || v.division, value: v.divID}))}
    })

    const selectedTeam = lazyRef<MutUiSelection<Guid, MinTeam | null>>()
    const teamOptions = computed(() => {
      const teams = [
        ...menu.value.get(selectedSeason.value.selectedKey)
          ?.competitions
          .get(selectedCompetition.value.selectedKey)
          ?.divisions
          .get(selectedDivision.value.selectedKey)
          ?.teams.values() ?? []
      ].sort(
        sortBy(v => v.teamLetter)
      )

      return teams.length === 0
        ? noAvailableOptions()
        : {disabled: false, options: teams.map(v => ({label: teamDesignationAndMaybeName(v), value: v.teamID}))}
    })

    /**
     * this assumes that the "selected<X>" value has already been updated, and we are reacting to the change immediately after the fact
     */
    const handleMenuSelectionUpdate = (v: "season" | "competition" | "division" | "team") : void => {
      switch (v) {
        case "season":
          // season changed, might need to update selected comp
          selectedCompetition.value.selectedKey = competitionOptions.value.options.find(v => v.value === selectedCompetition.value.selectedKey)?.value
            || forceCheckedIndexedAccess(competitionOptions.value.options, 0)?.value
            || "";
          // fallthrough
        case "competition":
          // comp changed, might need to update selected div
          selectedDivision.value.selectedKey = divisionOptions.value.options.find(v => v.value === selectedDivision.value.selectedKey)?.value
            || forceCheckedIndexedAccess(divisionOptions.value.options, 0)?.value
            || "";
          // fallthrough
        case "division":
          // div changed, might need to update selected team
          selectedTeam.value.selectedKey = teamOptions.value.options.find(v => v.value === selectedTeam.value.selectedKey)?.value
            || forceCheckedIndexedAccess(teamOptions.value.options, 0)?.value
            || "";
          // fallthrough
        case "team":
          // no work to do, this is the "leaf" of the menu hierarchy
          return;
        default: exhaustiveCaseGuard(v)
      }
    }

    const teamSummaryForFocusedSeasonCompDiv = (() => {
      const resolver = ReactiveReifiedPromise<CoachAssignmentsTeamSummaryTeam[]>()

      const load = (args: {seasonUID: Guid, competitionUID: Guid, divID: Guid}) => {
        resolver.run(() => getCoachAssignmentsTeamsSummary(axiosAuthBackgroundInstance, args))
      }

      const tryLoadFromContext = () => {
        const seasonUID = selectedSeason.value.selectedObj?.seasonUID
        const competitionUID = selectedCompetition.value.selectedObj?.competitionUID
        const divID = selectedDivision.value.selectedObj?.divID

        if (seasonUID && competitionUID && divID) {
          load({seasonUID, competitionUID, divID})
        }
      }

      return {
        load,
        tryLoadFromContext,
        get value() { return resolver.underlying }
      }
    })()

    const existingAssignmentsForFocusedTeam = (() => {
      const resolver = ReactiveReifiedPromise<{users: CoachAssignmentUser[], toDeleteForm: VolAssignmentsToDeleteForm}>()

      const load = (args: {seasonUID: Guid, teamID: Guid}) => {
        resolver.run(async () => {
          const users = await listCoachAssignmentVolunteers(axiosAuthBackgroundInstance, {seasonUID: args.seasonUID, teamID: args.teamID})
          const codeInfo = await getAssignableCodesForUsers(axiosAuthBackgroundInstance, {seasonUID: args.seasonUID, teamID: args.teamID, userIDs: users.map(v => v.userID)})
          return {
            users,
            toDeleteForm: VolAssignmentsToDeleteForm(codeInfo),
          }
        })
      }

      const tryLoadFromContext = () : void => {
        const seasonUID = selectedSeason.value.selectedObj?.seasonUID
        const teamID = selectedTeam.value.selectedObj?.teamID
        if (seasonUID && teamID) {
          load({seasonUID, teamID})
        }
      }

      return {
        tryLoadFromContext,
        pending: () => { resolver.forcePending() },
        reset: () => { resolver.reset() },
        get value() { return resolver.underlying },
      }
    })()

    type CandidateListingMode = "list" | "search"
    const coachAssignmentCandidatesForFocusedTeam = (() => {
      const candidateResolver = ReactiveReifiedPromise<CoachAssignmentFormInfo>()
      const text = ref("")
      const requireHasChildInDivSeason = ref(false)
      const requireDeclaredIntentToBeCoachOrAssistantCoach = ref(true)
      const mode = ref<CandidateListingMode>("search")

      const tryLoadFromContext = () => {
        const seasonUID = selectedSeason.value.selectedObj?.seasonUID
        const competitionUID = selectedCompetition.value.selectedObj?.competitionUID
        const divID = selectedDivision.value.selectedObj?.divID
        const teamID = selectedTeam.value.selectedObj?.teamID
        const searchText = text.value

        if (!seasonUID || !competitionUID || !divID || !teamID || (mode.value === "search" && !searchText)) {
          return // shouldn't happen
        }

        candidateResolver.run(async () => {
          const candidates = await (async () => {
            if (mode.value === "list") {
              return await listCoachAssignmentCandidates(axiosAuthBackgroundInstance, {
                seasonUID,
                competitionUID,
                divID,
                requireHasChildInDivSeason: requireHasChildInDivSeason.value,
              })
            }
            else if (mode.value === "search") {
              return await searchCoachAssignmentCandidates(axiosAuthBackgroundInstance, {
                seasonUID,
                competitionUID,
                divID,
                searchText,
                requireHasChildInDivSeason: requireHasChildInDivSeason.value,
                requireDeclaredIntentToBeCoachOrAssistantCoach: requireDeclaredIntentToBeCoachOrAssistantCoach.value,
              })
            }
            else {
              exhaustiveCaseGuard(mode.value)
            }
          })()

          const formData = (() => {
            const r : {[userID: Guid]: {teamID: Guid, code: string}} = {}
            for (const candidate of candidates) {
              r[candidate.userID] = {teamID: teamID, code: ""}
            }
            return r;
          })()

          // we could probably scan through and pluck out the right things on a per user basis, but just adding a reference
          // to the codeInfo from each candidate is ergonomic enough (and may be cheaper since we share the 1 obj ref across all instances)
          const codeInfo = await getAssignableCodesForUsers(axiosAuthBackgroundInstance, {seasonUID, teamID, userIDs: candidates.map(v => v.userID)})

          return {
            codeInfo,
            formData,
            candidates,
          }
        })
      }

      return {
        get candidateFormInfo() { return candidateResolver.underlying },
        tryLoadFromContext,
        pending: () => candidateResolver.forcePending(),
        reset: () => candidateResolver.reset(),
        searchText: text,
        requireHasChildInDivSeason,
        requireDeclaredIntentToBeCoachOrAssistantCoach,
        get mode() { return mode.value },
        set mode(v) { mode.value = v },
      }
    })()

    onMounted(async () => {
      await GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        const coreComp = await getCompetitions(axiosAuthBackgroundInstance, {includeDisabled: false}).then(vs => {
          return vs.find(v => v.competitionID /*weakEq*/ == 1)
        })

        const allOptions = await getCoachAssignmentMenuOptions(axiosInstance)

        menu.value = (() => {
          const seasonsDistinct = allOptions
            .map(v => v.season)
            .filter(uniqueBy(v => v.seasonUID))
            .sort(sortBy(v => v.seasonID, "desc"));

          const ss : MenuSeasons = new Map(
            seasonsDistinct.map(rootSeason => {
              const seasonRelevantTeams = allOptions.filter(v => v.season.seasonUID === rootSeason.seasonUID).map(v => v.team)
              const seasonRelevantCompsDistinct = seasonRelevantTeams.map(v => v.competition).filter(uniqueBy(v => v.competitionUID)).sort(sortBy(v => v.competitionID))

              const cs : MenuComps = new Map(
                seasonRelevantCompsDistinct.map(seasonComp => {
                  const seasonCompRelevantTeams = seasonRelevantTeams.filter(v => v.competition.competitionUID === seasonComp.competitionUID)
                  const seasonCompRelevantDivsDistinct = seasonCompRelevantTeams.map(v => v.division).filter(uniqueBy(v => v.divID)).sort(sortByMany(
                    sortBy(v => v.divNum),
                    sortBy(v => v.gender)
                  ))

                  const ds : MenuDivs = new Map(seasonCompRelevantDivsDistinct.map(seasonCompDiv => {
                    const teams = seasonCompRelevantTeams.filter(v => v.division.divID === seasonCompDiv.divID).sort(sortBy(v => v.teamName));

                    const ts : MenuTeams = new Map(teams.map(team => [team.teamID, team]))

                    return [seasonCompDiv.divID, {division: seasonCompDiv, teams: ts}]
                  }))

                  return [seasonComp.competitionUID, {competition: seasonComp, divisions: ds}]
                })
              )

              return [rootSeason.seasonUID, {season: rootSeason, competitions: cs}];
            })
          );

          return ss;
        })()

        selectedSeason.value = MutUiSelection(
          seasonUID => menu.value.get(seasonUID)?.season ?? null,
          seasonOptions.value.options.find(opt => opt.value === coreComp?.seasonUID)?.value // current season for core comp
            || forceCheckedIndexedAccess(seasonOptions.value.options, 0)?.value // first available option
            || "" // no good choice
        )

        selectedCompetition.value = MutUiSelection(
          compUID => menu.value.get(selectedSeason.value.selectedKey)?.competitions.get(compUID)?.competition ?? null,
          forceCheckedIndexedAccess(competitionOptions.value.options, 0)?.value || ""
        )

        selectedDivision.value = MutUiSelection(
          divID => menu.value.get(selectedSeason.value.selectedKey)
            ?.competitions
            .get(selectedCompetition.value.selectedKey)
            ?.divisions
            .get(divID)?.division ?? null,
          forceCheckedIndexedAccess(divisionOptions.value.options, 0)?.value || ""
        )

        selectedTeam.value = MutUiSelection(
          teamID => menu.value.get(selectedSeason.value.selectedKey)
            ?.competitions
            .get(selectedCompetition.value.selectedKey)
            ?.divisions
            .get(selectedDivision.value.selectedKey)
            ?.teams
            .get(teamID) ?? null,
          forceCheckedIndexedAccess(teamOptions.value.options, 0)?.value || ""
        )

        naiveFullReload({
          teamSummaryForFocusedSeasonCompDiv: true,
          existingAssignmentsForFocusedTeam: true,
          coachAssignmentCandidatesForFocusedTeam: true,
        })

        ready.value = true
      })
    })

    const doCreateCoachAndVolAssignments = async () : Promise<void> => {
      await GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        const seasonUID = selectedSeason.value.selectedObj?.seasonUID
        const teamID = selectedTeam.value.selectedObj?.teamID
        const formInfo = coachAssignmentCandidatesForFocusedTeam.candidateFormInfo.getOrNull()

        if (!seasonUID || !teamID || !formInfo) {
          return // shouldn't happen
        }

        const form = formInfo.formData
        const userIsSubmittable = (() => {
          const usersByUserID = new Map(formInfo.candidates.map(v => [v.userID, v]))
          return (userID: Guid) => {
            const user = usersByUserID.get(userID)
            return user
              ? !user.riskStatusCoachResult.forbidden // "NOT forbidden", meaning "is submittable"
              : false // shouldn't happen
          }
        })();

        try {
          const codes : {userID: Guid, teamID: Guid, code: string}[] = Object
              .entries(form)
              .filter(([userID, _data]) => userIsSubmittable(userID))
              .map(([userID, data]) => {
                return data && data.code // where "no selected code" means "was not selected at all"
                  ? {userID, teamID: data.teamID, code: data.code}
                  : null
              })
              .filter(v => !!v) // drop the nulls

          await createCoachAndVolunteerAssignments(axiosAuthBackgroundInstance, {
            seasonUID,
            codes,
          })

          iziToast.success({message: `Created ${codes.length} ${codes.length === 1 ? "assignment" : "assignments"}`})

          naiveFullReload({
            teamSummaryForFocusedSeasonCompDiv: true,
            existingAssignmentsForFocusedTeam: true,
            coachAssignmentCandidatesForFocusedTeam: true,
          })
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      })
    }

    const naiveFullReload = (what: {
      teamSummaryForFocusedSeasonCompDiv: boolean,
      existingAssignmentsForFocusedTeam: boolean,
      coachAssignmentCandidatesForFocusedTeam: boolean,
    }) : void => {
      if (what.teamSummaryForFocusedSeasonCompDiv) {
        teamSummaryForFocusedSeasonCompDiv.tryLoadFromContext()
      }
      if (what.existingAssignmentsForFocusedTeam) {
        existingAssignmentsForFocusedTeam.tryLoadFromContext()
      }
      if (what.coachAssignmentCandidatesForFocusedTeam) {
        coachAssignmentCandidatesForFocusedTeam.tryLoadFromContext()
      }
    }

    const confirmDeleteTeamAssignmentsModalController = (() => {
      const {busy, onCloseCB} = useDefaultNoCloseModalIfBusy()

      const doDelete = async () : Promise<void> => {
        try {
          const data = existingAssignmentsForFocusedTeam.value.getOrNull()
          if (!data) {
            return; // shouldn't happen
          }

          const deleteCount = data.toDeleteForm.selectedCount

          try {
            busy.value = true

            await deleteCoachAndVolunteerAssignments(axiosAuthBackgroundInstance, {
              coachAssignmentIDs: [...data.toDeleteForm.coachAssignmentIDs],
              volunteerAssignmentIDs: [...data.toDeleteForm.volunteerAssignmentIDs],
              youthCoachAssignmentIDs: [...data.toDeleteForm.youthCoachAssignmentIDs],
            })
          }
          finally {
            busy.value = false
          }

          iziToast.success({message: `Deleted ${deleteCount} ${deleteCount === 1 ? "assignment" : "assignments"}`})
          naiveFullReload({
            teamSummaryForFocusedSeasonCompDiv: true,
            existingAssignmentsForFocusedTeam: true,
            coachAssignmentCandidatesForFocusedTeam: true,
          })

          confirmDeleteTeamAssignmentsModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<void>({
        title: () => <>
          <div>Delete Team Assignments?</div>
          <div class="my-2 border-b"/>
        </>,
        content: () => {
          const data = existingAssignmentsForFocusedTeam.value.getOrNull()
          if (!data) {
            return null // shouldn't happen
          }
          return <div>
            <ConfirmDeleteTeamAssignmentsModal
              data={data.toDeleteForm}
              onConfirm={() => { doDelete() }}
              onCancel={() => { confirmDeleteTeamAssignmentsModalController.close() }}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB})
    })();

    return () => {
      if (!ready.value) {
        return null
      }

      return <div style="--fk-padding-input:.35em; --fk-bg-input: white; --fk-margin-outer: none;">
        <AutoModal controller={confirmDeleteTeamAssignmentsModalController} data-test="ConfirmDeleteTeamAssignmentsModal"/>
        <div style="display:grid; grid-template-columns: max-content auto; align-items:center; grid-gap: .5em .5em;">
          <div class="align-center">Season</div>
          <FormKit
            type="select"
            disabled={seasonOptions.value.disabled}
            options={seasonOptions.value.options}
            v-model={selectedSeason.value.selectedKey}
            delay={0}
            data-test="seasonUID"
            onInput={(value : any) => {
              if (selectedSeason.value.selectedKey === value) {
                return
              }
              selectedSeason.value.selectedKey = value
              handleMenuSelectionUpdate("season")
              naiveFullReload({
                teamSummaryForFocusedSeasonCompDiv: true,
                existingAssignmentsForFocusedTeam: true,
                coachAssignmentCandidatesForFocusedTeam: true,
              })
            }}
          />
          <div>Competition</div>
          <div>
            <FormKit
              type="select"
              disabled={competitionOptions.value.disabled}
              options={competitionOptions.value.options}
              v-model={selectedCompetition.value.selectedKey}
              delay={0}
              onInput={(value : any) => {
                if (selectedCompetition.value.selectedKey === value) {
                  return
                }
                selectedCompetition.value.selectedKey = value
                handleMenuSelectionUpdate("competition")
                naiveFullReload({
                  teamSummaryForFocusedSeasonCompDiv: true,
                  existingAssignmentsForFocusedTeam: true,
                  coachAssignmentCandidatesForFocusedTeam: true,
                })
              }}
              data-test="competitionUID"
            />
          </div>
          <div>Division</div>
          <div>
            <FormKit
              type="select"
              disabled={divisionOptions.value.disabled}
              options={divisionOptions.value.options}
              v-model={selectedDivision.value.selectedKey}
              delay={0}
              onInput={(value: any) => {
                if (selectedDivision.value.selectedKey === value) {
                  return
                }
                selectedDivision.value.selectedKey = value
                handleMenuSelectionUpdate("division")
                naiveFullReload({
                  teamSummaryForFocusedSeasonCompDiv: true,
                  existingAssignmentsForFocusedTeam: true,
                  coachAssignmentCandidatesForFocusedTeam: true,
                })
              }}
              data-test="divID"
            />
          </div>
          <div>Team</div>
          <div>
            <FormKit
              type="select"
              disabled={teamOptions.value.disabled}
              options={teamOptions.value.options}
              v-model={selectedTeam.value.selectedKey}
              onInput={(value: any) => {
                if (selectedTeam.value.selectedKey === value) {
                  return
                }
                selectedTeam.value.selectedKey = value
                handleMenuSelectionUpdate("team")
                naiveFullReload({
                  teamSummaryForFocusedSeasonCompDiv: false,
                  existingAssignmentsForFocusedTeam: true,
                  coachAssignmentCandidatesForFocusedTeam: true,
                })
              }}
              data-test="teamID"
            />
          </div>
        </div>

        <div class="border rounded-md overflow-x-auto my-4 shadow-md">
          <div class="px-2 bg-green-800 text-white rounded-t-md">Existing Assignments {selectedTeam.value.selectedObj ? " - " + teamDesignationAndMaybeName(selectedTeam.value.selectedObj) : null}</div>
          <div class="px-2 py-1">
            <CurrentTeamAssignmentsListingElement
              team={selectedTeam.value.selectedObj}
              data={existingAssignmentsForFocusedTeam.value}
              onDelete={() => confirmDeleteTeamAssignmentsModalController.open()}
            />
          </div>
        </div>

        <div class="border my-4 rounded-md shadow-md bg-white">
          <div class="px-2 bg-green-800 text-white rounded-t-md">Assignment Search</div>
          <div class="px-2 py-1">
            <div class="flex gap-2 items-center">
              <label class="flex gap-2 items-center">
                <input
                  type="radio"
                  class="transition"
                  name="candidateSearchType"
                  checked={coachAssignmentCandidatesForFocusedTeam.mode === "search"}
                  value={"search" satisfies CandidateListingMode}
                  onInput={() => {
                    coachAssignmentCandidatesForFocusedTeam.mode = "search"
                    coachAssignmentCandidatesForFocusedTeam.reset()
                  }}
                  data-test="candidateSearchType:search"
                />
                Search
              </label>
              <label class="flex gap-2 items-center">
                <input
                  type="radio"
                  class="transition"
                  name="candidateSearchType"
                  checked={coachAssignmentCandidatesForFocusedTeam.mode === "list"}
                  value={"list" satisfies CandidateListingMode}
                  onInput={() => {
                    coachAssignmentCandidatesForFocusedTeam.mode = "list"
                    coachAssignmentCandidatesForFocusedTeam.tryLoadFromContext() // when entering list mode, just "load the list"
                  }}
                  data-test="candidateSearchType:list"
                />
                List
              </label>
            </div>
              {(() => {
                const s = selectedSeason.value.selectedObj
                const d = selectedDivision.value.selectedObj
                const t = selectedTeam.value.selectedObj
                return <div class="mt-2">
                  {(s && d && t)
                    ? <CoachAssignmentCandidateSearch
                        data-test-focusedTeam={t.teamID}
                        season={s}
                        division={d}
                        team={t}
                        mode={coachAssignmentCandidatesForFocusedTeam.mode}
                        searchText={coachAssignmentCandidatesForFocusedTeam.searchText}
                        requireHasChildInDivSeason={coachAssignmentCandidatesForFocusedTeam.requireHasChildInDivSeason.value}
                        requireDeclaredIntentToBeCoachOrAssistantCoach={coachAssignmentCandidatesForFocusedTeam.requireDeclaredIntentToBeCoachOrAssistantCoach.value}
                        candidateFormInfo={coachAssignmentCandidatesForFocusedTeam.candidateFormInfo}
                        onSearch={() => coachAssignmentCandidatesForFocusedTeam.tryLoadFromContext()}
                        onUpdateCoachAndVolAssignments={() => doCreateCoachAndVolAssignments()}
                        onUpdate:requireHasChildInDivSeason={v => {
                          coachAssignmentCandidatesForFocusedTeam.requireHasChildInDivSeason.value = v
                          if (coachAssignmentCandidatesForFocusedTeam.mode === "list") {
                            // if we're in "list" mode, and we update this checkbox, then we want to re-get the listing
                            coachAssignmentCandidatesForFocusedTeam.tryLoadFromContext()
                          }
                        }}
                        onUpdate:requireDeclaredIntentToBeCoachOrAssistantCoach={v => {
                          coachAssignmentCandidatesForFocusedTeam.requireDeclaredIntentToBeCoachOrAssistantCoach.value = v
                          if (coachAssignmentCandidatesForFocusedTeam.mode === "list") {
                            // if we're in "list" mode, and we update this checkbox, then we want to re-get the listing
                            coachAssignmentCandidatesForFocusedTeam.tryLoadFromContext()
                          }
                        }}
                      />
                    : <div>Select a team.</div>
                  }
                </div>
              })()}
          </div>
        </div>

        <div class="border my-4 rounded-md shadow-md bg-white">
          <div class="px-2 bg-green-800 text-white rounded-t-md">Teams Overview</div>
          <div class="px-2 py-1">
            <TeamSummaryElement class="my-2" data={teamSummaryForFocusedSeasonCompDiv.value}/>
          </div>
        </div>
      </div>
    }
  }
})
